import React, { useState, useEffect } from 'react';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import engineerService from '../../services/engineerService';

const KnowledgeBaseView = () => {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    
    const categoriesResult = await engineerService.getKnowledgeBaseCategories();
    if (categoriesResult.success) {
      setCategories(categoriesResult.data);
    }
    
    const articlesResult = await engineerService.getKnowledgeBaseArticles();
    if (articlesResult.success) {
      setArticles(articlesResult.data);
    }
    
    const contactsResult = await engineerService.getEmergencyContacts();
    if (contactsResult.success) {
      setContacts(contactsResult.data);
    }
    
    setLoading(false);
  };

  const handleSearch = async () => {
    const result = await engineerService.getKnowledgeBaseArticles(searchTerm);
    if (result.success) {
      setArticles(result.data);
      if (result.data.length === 0) {
        error('No articles found');
      }
    }
  };

  const handleReadArticle = async (article) => {
    const result = await engineerService.getKnowledgeBaseArticle(article.id);
    if (result.success) {
      setSelectedArticle(result.data);
      setShowArticleModal(true);
    }
  };

  // Styles
  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  };

  const searchContainerStyle = {
    display: 'flex',
    gap: '8px',
    flex: '1',
    minWidth: '250px',
    maxWidth: '500px'
  };

  const categoriesGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const categoryCardStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  const cardStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  };

  const articleCardStyle = {
    background: 'var(--background)',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    marginBottom: '16px'
  };

  const categoryBadgeStyle = {
    padding: '4px 12px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    background: 'var(--primary)',
    color: 'white',
    textTransform: 'uppercase',
    display: 'inline-block'
  };

  const contactCardStyle = {
    background: 'var(--background)',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid var(--border)'
  };

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  };

  const modalContentStyle = {
    background: 'var(--surface)',
    borderRadius: '12px',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
  };

  const modalHeaderStyle = {
    padding: '24px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    background: 'var(--surface)',
    zIndex: 10
  };

  const modalBodyStyle = {
    padding: '24px'
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading knowledge base...
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {/* Header with Search */}
      <div style={headerStyle}>
        <div style={searchContainerStyle}>
          <Input
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button variant="primary" onClick={handleSearch}>
            Search
          </Button>
        </div>
      </div>

      {/* Quick Links Categories */}
      <div style={categoriesGridStyle}>
        {categories.map((category) => (
          <div
            key={category.id}
            style={categoryCardStyle}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            {/* <div style={{ fontSize: '48px', marginBottom: '12px' }}>[{category.icon}]</div> */}
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
              {category.name}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {category.articleCount} articles
            </div>
          </div>
        ))}
      </div>

      {/* Popular Articles */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px' }}>
          Popular Articles
        </h3>
        {articles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            No articles found
          </div>
        ) : (
          <div>
            {articles.map((article) => (
              <div
                key={article.id}
                style={articleCardStyle}
                onClick={() => handleReadArticle(article)}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(4px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={categoryBadgeStyle}>{article.category}</span>
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
                      {article.title}
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                      {article.description}
                    </div>
                    <div style={{ display: 'flex', gap: '24px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      <div>{article.views} views</div>
                      <div>{article.helpful} found helpful</div>
                    </div>
                  </div>
                  <Button variant="secondary">
                    Read Article
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Emergency Contacts */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px' }}>
          Emergency Contacts
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          {contacts.map((contact) => (
            <div key={contact.id} style={contactCardStyle}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase' }}>
                {contact.title}
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                {contact.name}
              </div>
              <a
                href={`tel:${contact.phone}`}
                style={{
                  fontSize: '14px',
                  color: 'var(--primary)',
                  fontWeight: '600',
                  textDecoration: 'none',
                  display: 'block',
                  marginBottom: '4px'
                }}
              >
                {contact.phone}
              </a>
              <a
                href={`mailto:${contact.email}`}
                style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  textDecoration: 'none'
                }}
              >
                {contact.email}
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Article Modal */}
      {showArticleModal && selectedArticle && (
        <div style={modalOverlayStyle} onClick={() => setShowArticleModal(false)}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <div>
                <span style={categoryBadgeStyle}>{selectedArticle.category}</span>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', margin: '12px 0 0 0' }}>
                  {selectedArticle.title}
                </h2>
              </div>
              <button
                onClick={() => setShowArticleModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  padding: '0 8px'
                }}
              >
                ×
              </button>
            </div>
            <div style={modalBodyStyle}>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                {selectedArticle.description}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: '1.8' }}>
                {selectedArticle.content}
              </div>
              <div style={{ marginTop: '24px', padding: '16px', background: 'var(--background)', borderRadius: '8px', display: 'flex', gap: '24px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                <div>{selectedArticle.views} views</div>
                <div>{selectedArticle.helpful} found helpful</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseView;